import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Package, Gift, ShoppingBag, Sparkles, Filter, Zap,
  TrendingUp, Award, Loader2, Store,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import ItemCard, { ItemDetail } from '../components/ItemCard';
import { EquipmentPanel } from '../components/EquipSlot';
import GiftModal, { GiftsInbox } from '../components/GiftModal';
import { Sheet, SkeletonList, Empty, SectionHead } from '../components/ui';
import { rarityOf, SLOT_THEME, themeOf } from '../lib/classTheme';

const TABS = [
  { k: 'gear', label: 'Gear', icon: Package },
  { k: 'gifts', label: 'Gifts', icon: Gift },
];

export default function Inventory() {
  const qc = useQueryClient();
  const { user, hero, load } = useAuth();
  const [tab, setTab] = useState('gear');
  const [filterSlot, setFilterSlot] = useState('');
  const [filterRarity, setFilterRarity] = useState('');
  const [selected, setSelected] = useState(null);
  const [giftItem, setGiftItem] = useState(null);
  const [slotPicker, setSlotPicker] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => api.get('/inventory').then(r => r.data),
  });

  const { data: gifts } = useQuery({
    queryKey: ['gifts'],
    queryFn: () => api.get('/inventory/gifts').then(r => r.data),
    refetchInterval: tab === 'gifts' ? 20000 : false,
  });

  const invalidate = () => {
    qc.invalidateQueries(['inventory']);
    qc.invalidateQueries(['gifts']);
    qc.invalidateQueries(['hero']);
    load();
  };

  const equipM = useMutation({
    mutationFn: (id) => api.post(`/inventory/equip/${id}`),
    onSuccess: ({ data: d }) => { toast.success(`⚔️ ${d.equipped} equipped`); setSelected(null); setSlotPicker(null); invalidate(); },
    onError: e => toast.error(e.message),
  });

  const unequipM = useMutation({
    mutationFn: (slot) => api.post(`/inventory/unequip/${slot}`),
    onSuccess: () => { toast('Unequipped'); setSelected(null); invalidate(); },
    onError: e => toast.error(e.message),
  });

  const autoM = useMutation({
    mutationFn: () => api.post('/inventory/auto-equip'),
    onSuccess: ({ data: d }) => { toast.success(`✨ ${d.equipped.length} items equipped`); invalidate(); },
    onError: e => toast.error(e.message),
  });

  const sellM = useMutation({
    mutationFn: (id) => api.post(`/inventory/sell/${id}`),
    onSuccess: ({ data: d }) => { toast.success(`Sold for 💎${d.refund}`); setSelected(null); invalidate(); },
    onError: e => toast.error(e.message),
  });

  const acceptM = useMutation({
    mutationFn: (id) => api.post(`/inventory/gifts/${id}/accept`),
    onSuccess: ({ data: d }) => { toast.success(`🎁 ${d.item} added!`); invalidate(); },
    onError: e => toast.error(e.message),
  });

  const declineM = useMutation({
    mutationFn: (id) => api.post(`/inventory/gifts/${id}/decline`),
    onSuccess: () => { toast('Gift declined'); invalidate(); },
  });

  const items = data?.items || [];
  const filtered = useMemo(() => items.filter(i =>
    (!filterSlot || i.slot === filterSlot) &&
    (!filterRarity || i.rarity === filterRarity)
  ), [items, filterSlot, filterRarity]);

  const slotItems = useMemo(
    () => slotPicker ? items.filter(i => i.slot === slotPicker) : [],
    [items, slotPicker]);

  const t = themeOf(hero?.class_key);

  if (isLoading) return <SkeletonList n={4} h="h-40" />;

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card p-4 overflow-hidden relative"
        style={{ background: t.radial }}>
        <div className="relative flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${t.gradient}
            grid place-items-center text-white shadow-lg`}>
            <Package size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-xl font-extrabold">Inventory</h1>
            <p className="text-xs text-ink-500 font-semibold">
              {data?.stats?.total || 0} items · {data?.equipment?.gearScore || 0} gear score
            </p>
          </div>
          <Link to="/item-shop" className="btn-primary btn-sm shrink-0">
            <Store size={14} /> Shop
          </Link>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-3">
          <Stat icon={Package} v={data?.stats?.total || 0} l="Items" />
          <Stat icon={Sparkles} v={`${data?.equipment?.equippedCount || 0}/5`} l="Equipped" />
          <Stat icon={TrendingUp} v={data?.equipment?.gearScore || 0} l="Gear" />
          <Stat icon={Award} v={`💎${compact(user?.points_balance || 0)}`} l="Points" />
        </div>
      </div>

      {/* ═══ TABS ═══ */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100">
        {TABS.map(x => (
          <button key={x.k} onClick={() => setTab(x.k)}
            className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg
              text-xs font-bold transition ${tab === x.k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>
            <x.icon size={13} /> {x.label}
            {x.k === 'gifts' && data?.pendingGifts > 0 && (
              <span className="absolute top-1 right-2 w-4 h-4 rounded-full bg-red-500
                text-white text-[9px] font-black grid place-items-center">
                {data.pendingGifts}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ═══ GEAR ═══ */}
      {tab === 'gear' && (
        <>
          <EquipmentPanel
            hero={hero}
            equipment={data?.equipment}
            onSlotClick={setSlotPicker}
            onUnequip={(slot) => unequipM.mutate(slot)}
          />

          {data?.stats?.equippedSlots < 5 && items.length > 0 && (
            <button onClick={() => autoM.mutate()} disabled={autoM.isPending}
              className="btn-mint w-full">
              {autoM.isPending
                ? <><Loader2 size={15} className="animate-spin" /> Equipping…</>
                : <><Zap size={15} /> Auto-equip best gear</>}
            </button>
          )}

          {/* filters */}
          <div className="space-y-2 sticky top-14 z-20 py-2 -mx-3 px-3 bg-ink-50/95 backdrop-blur">
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              <FilterChip active={!filterSlot} onClick={() => setFilterSlot('')} label="All" />
              {Object.entries(SLOT_THEME).map(([k, s]) => (
                <FilterChip key={k} active={filterSlot === k} onClick={() => setFilterSlot(k)}
                  label={`${s.icon} ${s.label}`} />
              ))}
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              <FilterChip active={!filterRarity} onClick={() => setFilterRarity('')} label="All rarities" />
              {['common', 'uncommon', 'rare', 'epic', 'legendary'].map(k => {
                const r = rarityOf(k);
                return (
                  <button key={k} onClick={() => setFilterRarity(filterRarity === k ? '' : k)}
                    className="px-3 py-1.5 rounded-lg text-xs font-black whitespace-nowrap transition border"
                    style={filterRarity === k
                      ? { background: r.hex, color: '#fff', borderColor: r.hex }
                      : { background: '#fff', color: r.hex, borderColor: `${r.hex}55` }}>
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!filtered.length ? (
            <Empty icon={Package} title="No items here"
              sub={items.length ? 'Try different filters.' : 'Buy gear or win it in the arena.'}
              action={<Link to="/item-shop" className="btn-primary btn-sm">Open shop</Link>} />
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {filtered.map(it => (
                <ItemCard key={it.id} item={it}
                  owned equipped={it.equipped} locked={it.locked}
                  onClick={() => setSelected(it)} />
              ))}
            </div>
          )}

          {/* rarity breakdown */}
          {data?.stats?.byRarity?.length > 0 && (
            <div className="card p-3.5">
              <p className="label">Collection</p>
              <div className="flex flex-wrap gap-1.5">
                {data.stats.byRarity.map(b => (
                  <span key={b.rarity} className="chip"
                    style={{ background: `${b.color_hex}22`, color: b.color_hex }}>
                    {b.label} × {b.c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ GIFTS ═══ */}
      {tab === 'gifts' && (
        <GiftsInbox gifts={gifts}
          onAccept={(id) => acceptM.mutate(id)}
          onDecline={(id) => declineM.mutate(id)}
          busy={acceptM.isPending || declineM.isPending} />
      )}

      {/* ═══ ITEM DETAIL SHEET ═══ */}
      <Sheet open={!!selected} onClose={() => setSelected(null)} title={selected?.name || ''}>
        {selected && (
          <ItemDetail item={selected}
            busy={equipM.isPending || sellM.isPending || unequipM.isPending}
            onEquip={() => equipM.mutate(selected.id)}
            onUnequip={() => unequipM.mutate(selected.slot)}
            onSell={() => confirm(`Sell ${selected.name} for ${selected.sellPrice} points?`)
              && sellM.mutate(selected.id)}
            onGift={() => { setGiftItem(selected); setSelected(null); }} />
        )}
      </Sheet>

      {/* ═══ SLOT PICKER ═══ */}
      <Sheet open={!!slotPicker} onClose={() => setSlotPicker(null)}
        title={slotPicker ? `Choose ${SLOT_THEME[slotPicker].label}` : ''} wide>
        {slotPicker && (
          !slotItems.length ? (
            <Empty icon={Package} title={`No ${SLOT_THEME[slotPicker].label.toLowerCase()} yet`}
              sub={SLOT_THEME[slotPicker].hint}
              action={<Link to={`/item-shop?slot=${slotPicker}`} className="btn-primary btn-sm">
                Browse shop</Link>} />
          ) : (
            <div className="grid grid-cols-3 gap-2.5">
              {slotItems.map(it => (
                <ItemCard key={it.id} item={it} owned equipped={it.equipped}
                  locked={it.locked} selected={it.equipped}
                  onClick={() => !it.equipped && equipM.mutate(it.id)} />
              ))}
            </div>
          )
        )}
      </Sheet>

      {/* ═══ GIFT MODAL ═══ */}
      <GiftModal open={!!giftItem} onClose={() => setGiftItem(null)}
        item={giftItem} onSuccess={invalidate} />
    </div>
  );
}

function Stat({ icon: Icon, v, l }) {
  return (
    <div className="bg-white/70 backdrop-blur rounded-xl p-2 text-center">
      <Icon size={13} className="mx-auto text-ink-400 mb-0.5" />
      <p className="font-black text-sm">{v}</p>
      <p className="text-[9px] font-bold text-ink-400 uppercase">{l}</p>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
        ${active ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
      {label}
    </button>
  );
}